import { AuthError, signInWithRedirect, signOut } from '@aws-amplify/auth';
import { Box, Button, Divider, Link, Typography } from '@material-ui/core';
import React, { Dispatch, SetStateAction, forwardRef, useEffect, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { isAcceptedInviteSuccessfulSelector, getAcceptInviteErrorSelector } from '@redux/oauth/oauthSelectors';
import { CAIRNS_LOGO_IMAGE_URL } from 'assets/images/images';
import { EmailBoldIcon } from 'components/Icons';
import { AppleIcon } from 'components/Icons/AppleIcon';
import { GoogleIcon } from 'components/Icons/GoogleIcon';
import { PaneBottomContactPanel } from 'components/PaneBottomContactPanel/PaneBottomContactPanel';
import { amplifyGetOAuthUserData } from 'utils/amplify/amplify';
import { RouteNames } from 'utils/navigationUtils';
import { getQueryParamObject } from 'utils/util';
import { SUPPORT_EMAIL } from '../../../../../../configConsts';
import style from './FinishCreateAccountPaneV2.scss';

// Custom input component with maxLength
const CustomPhoneInput = forwardRef<HTMLInputElement, any>((props, ref) => {
  return <input {...props} ref={ref} maxLength={14} />; // Max length for formatted US phone: (555) 555-5555
});
CustomPhoneInput.displayName = 'CustomPhoneInput';

interface Props {
  acceptInvite: Function;
  login: Function;
  requestSmsVerification: Function;
  handleError: (err: any) => void;
  loadingState: [boolean, Dispatch<SetStateAction<boolean>>];
  handleExternalRouting: (url?: Nullable<string>) => void;
}

export const FinishCreateAccountPaneV2: React.FC<Props> = ({ acceptInvite, login, requestSmsVerification, handleError, loadingState, handleExternalRouting }) => {
  const location = useLocation();
  const token: string = getQueryParamObject(location?.search)?.token;
  const email: string = getQueryParamObject(location?.search)?.email;
  const navigate = useNavigate();

  const isAcceptedInviteSuccessful = useSelector(isAcceptedInviteSuccessfulSelector);
  const getAcceptInviteError = useSelector(getAcceptInviteErrorSelector);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [touched, setTouched] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>('');

  const handleSocialSignInError = (error: Nullable<any> = null) => {
    handleError({
      message: error?.message || 'Social sign-in failed. Please try again or use other sign-in methods.',
      status: 400,
      name: 'SocialSignInError'
    });
  };

  useEffect(() => {
    loadingState[1](false);

    // Store token in sessionStorage for use after OAuth redirect
    if (token) {
      sessionStorage.setItem('invite_token', token);
    }

    const checkOAuthCallback = async () => {
      // Only process OAuth callback if we initiated an OAuth sign-in
      const oauthInitiated = sessionStorage.getItem('oauth_signin_initiated');
      // Retrieve stored token
      const storedToken = sessionStorage.getItem('invite_token');

      if (!oauthInitiated && !token && !email) {
        if (!isAcceptedInviteSuccessful) {
          handleSocialSignInError(getAcceptInviteError);
          return;
        }
        navigate(RouteNames.SUCCESS_AUTH);
        return;
      }

      // If OAuth wasn't initiated, don't process the callback
      if (!oauthInitiated) {
        return;
      }

      try {
        // Check for OAuth error in URL parameters
        const queryParams = getQueryParamObject(location?.search);
        if (queryParams?.error) {
          handleSocialSignInError();
          return;
        }

        const oauthUserData = await amplifyGetOAuthUserData(handleError);

        if (!oauthUserData) {
          console.error('Failed to retrieve OAuth user data');
          handleSocialSignInError();
          return;
        }

        // User successfully authenticated via OAuth
        loadingState[1](true);

        // Call acceptInvite with social sign-in data
        try {
          await acceptInvite(
            {
              email: oauthUserData.email,
              cognitoToken: oauthUserData.idToken,
              cognitoSub: oauthUserData.cognitoSub,
              providerName: oauthUserData.providerName,
              providerId: oauthUserData.providerId,
              emailVerified: oauthUserData.emailVerified,
              givenName: oauthUserData.givenName,
              familyName: oauthUserData.familyName,
              name: oauthUserData.name,
              isNewSignupFlow: true
            },
            storedToken
          );
        } catch (error) {
          console.error('Error during acceptInvite call:', error);
          return;
        }

        sessionStorage.removeItem('invite_token');
        navigate(RouteNames.SUCCESS_AUTH);
      } catch (error) {
        console.error('OAuth sign-in flow error:', error);
        handleSocialSignInError(error);
      } finally {
        sessionStorage.removeItem('oauth_signin_initiated');
        loadingState[1](false);
      }
    };

    checkOAuthCallback();
  }, []);

  const validatePhoneNumber = (phone: string | undefined): boolean => {
    if (!phone) {
      return false;
    }
    // For national format, add +1 before validating
    const phoneWithCountryCode = phone.startsWith('+') ? phone : `+1${phone}`;
    return isValidPhoneNumber(phoneWithCountryCode);
  };

  const handlePhoneChange = (phone: string | undefined) => {
    setPhoneNumber(phone || '');
    setApiError(''); // Clear API error when user changes phone number

    // Mark as touched if user has started typing
    if (phone) {
      setTouched(true);
    }

    // Validate in real-time once user has started typing
    if (phone) {
      if (!validatePhoneNumber(phone)) {
        setPhoneError('Please enter a valid US phone number');
      } else {
        setPhoneError('');
      }
    } else if (touched) {
      // Only show "required" error if field was touched before
      setPhoneError('Phone number is required');
    } else {
      setPhoneError('');
    }
  };

  const handlePhoneBlur = () => {
    setTouched(true);
    if (!phoneNumber) {
      setPhoneError('Phone number is required');
    } else if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid US phone number');
    } else {
      setPhoneError('');
    }
  };

  const isPhoneValid = validatePhoneNumber(phoneNumber);

  const handleContinue = async () => {
    if (!isPhoneValid) {
      setTouched(true);
      setPhoneError('Please enter a valid US phone number');
      return;
    }

    try {
      loadingState[1](true);
      setApiError('');

      const phoneWithCountryCode = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

      await requestSmsVerification(token, phoneWithCountryCode);

      // Successfully requested SMS verification, navigate to verification screen
      navigate(`${RouteNames.PHONE_VERIFICATION}?token=${token}&phoneNumber=${encodeURIComponent(phoneWithCountryCode)}`);
    } catch (error: any) {
      console.error('Error requesting SMS verification:', error);
      const errorMessage = error?.message || 'Failed to send verification code. Please try again.';
      setApiError(errorMessage);
    } finally {
      loadingState[1](false);
    }
  };

  const handleGoogleSignIn = async () => {
    handleSocialSignIn('Google');
  };

  const handleAppleSignIn = async () => {
    handleSocialSignIn('Apple');
  };

  const handleSocialSignIn = async (provider: 'Google' | 'Apple') => {
    try {
      loadingState[1](true);
      // Set flag to indicate we're initiating OAuth sign-in
      // Token is already stored in sessionStorage from useEffect
      sessionStorage.setItem('oauth_signin_initiated', 'true');
      await signInWithRedirect({
        provider
      });
    } catch (error) {
      if ((error as AuthError).name === 'UserAlreadyAuthenticatedException') {
        try {
          await signOut(); // signOut from cognito if user already authenticated
          await signInWithRedirect({
            provider
          });
        } catch (error) {
          console.error(`${provider} sign-in error:`, error);
          handleSocialSignInError(error);
        }
        return;
      }
      console.error(`${provider} sign-in error:`, error);
      handleSocialSignInError(error);
    } finally {
      loadingState[1](false);
    }
  };

  const handleEmailSignIn = () => {
    navigate(RouteNames.CREATE_PASSWORD, { state: { email, token } });
  };

  return (
    <React.Fragment>
      <Box className={style.paneContainer}>
        <Box className={style.paneContentWrapper}>
          <Box className={style.mobileLogoWrapper}>
            <img src={CAIRNS_LOGO_IMAGE_URL} alt="Cairns Health" />
          </Box>
          <Typography className={style.mainHeader}>Sign up to Cairns Companion app</Typography>

          {/* Phone Number Input */}
          <Box className={style.phoneInputWrapper}>
            <Box className={`${style.phoneInputContainer} ${(phoneError && touched) || apiError ? style.phoneInputContainerError : ''}`}>
              <Box className={style.countryCodeBox}>
                <Typography className={style.countryCode}>+1</Typography>
              </Box>
              <Box className={style.phoneInputRightSide}>
                <Typography className={`${style.phoneInputLabel} ${(phoneError && touched) || apiError ? style.phoneInputLabelError : ''}`}>Phone number</Typography>
                <Box className={style.phoneInputField}>
                  <PhoneInput
                    defaultCountry="US"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    international={false}
                    countryCallingCodeEditable={false}
                    inputComponent={CustomPhoneInput}
                  />
                </Box>
              </Box>
            </Box>
            {((phoneError && touched) || apiError) && <Typography className={style.phoneError}>{apiError || phoneError}</Typography>}
          </Box>

          <Typography className={style.confirmationText}>By continuing, you agree to receive a text message with a verification code. Message and data rates may apply.</Typography>

          {/* Continue Button */}
          <Button variant="contained" fullWidth className={style.continueButton} onClick={handleContinue} disabled={!isPhoneValid}>
            Continue
          </Button>

          {/* Divider with "Or" */}
          <Box className={style.dividerContainer}>
            <Divider className={style.divider} />
            <Typography className={style.dividerText}>Or</Typography>
            <Divider className={style.divider} />
          </Box>

          {/* Social Sign-in Buttons */}
          <Button variant="outlined" fullWidth className={style.socialButton} onClick={handleGoogleSignIn} startIcon={<GoogleIcon width={28} height={28} />}>
            Sign in with Google
          </Button>

          <Button variant="outlined" fullWidth className={style.socialButton} onClick={handleAppleSignIn} startIcon={<AppleIcon width={28} height={28} />}>
            Sign in with Apple
          </Button>

          <Button variant="outlined" fullWidth className={style.socialButton} onClick={handleEmailSignIn} startIcon={<EmailBoldIcon width={28} height={28} />}>
            Use my email
          </Button>
        </Box>
        {/* Footer */}
        <PaneBottomContactPanel email={SUPPORT_EMAIL} isMobileHidden />
      </Box>
    </React.Fragment>
  );
};
